import type {
  AdminFoodRequest,
  FoodRequestKindName,
  RecipeConfidenceName,
} from '@steado/admin-contract/foods';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircleIcon, ArrowRightIcon, CheckIcon, RotateCcwIcon, SearchIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { ScopePills } from '@/components/table/ScopePills';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth/auth-context';
import { useDebounced } from '@/lib/use-debounced';
import { cn } from '@/lib/utils';
import { invalidateCatalog } from '@/screens/catalog-history/catalog-history-query';
import { FilterSelect, Figures, StatusBanner } from '@/screens/catalog-history/history-parts';

import { BulkNames } from './BulkNames';
import { EmptyQueue } from './EmptyQueue';
import {
  approveName,
  approveNames,
  invalidateRequests,
  queueQueryOptions,
  researchAgain,
  summaryQueryOptions,
  undoApprovals,
} from './food-requests-query';
import type { FoodRequestsSearch, QueueStatus } from './food-requests-search';
import { NameRequestDrawer } from './NameRequestDrawer';
import {
  KIND_LABELS,
  checksOf,
  confidenceOf,
  daysWaiting,
  plural,
  proposalSubOf,
  typedSubOf,
  waited,
} from './request-copy';
import { ADMIN_ONLY, ConfidenceDot, KindBadge, Kbd } from './review-parts';

export interface FoodRequestsScreenProps {
  readonly search: FoodRequestsSearch;
  readonly onSearchChange: (next: FoodRequestsSearch) => void;
  /** Opens a new food or a fix on its own page. Navigating is the route's job. */
  readonly onOpenRequest: (id: string) => void;
}

/** What the last action did, said once, with Undo when it can be undone. */
export interface QueueNotice {
  readonly text: string;
  readonly undo?: readonly { readonly batchId: string | null; readonly requestId: string }[];
}

type KindScope = FoodRequestKindName | 'all';

const ANY = '__any__';

const STATUS_OPTIONS: readonly { value: QueueStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'researching', label: 'Being researched' },
  { value: 'resolved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const CONFIDENCE_OPTIONS = [
  { value: ANY, label: 'Any' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STATUS_TITLES: Readonly<Record<QueueStatus, string>> = {
  open: 'Open requests',
  researching: 'Being researched',
  resolved: 'Approved requests',
  rejected: 'Rejected requests',
};

/** The table's columns, the design's widths: a row is one line of a grid shared with its header. */
const GRID =
  'grid grid-cols-[44px_minmax(150px,1fr)_96px_minmax(200px,2.2fr)_120px_100px_88px_132px] items-center';

const HEADER_CELL = 'px-3 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase';

/**
 * The food request queue: what people logged that the catalog did not know,
 * or knew by another name. Ready to approve first (a name no other food
 * answers to, one click each), then the ones that need a look, most people
 * first; requests still being researched wait under the table until their
 * sources are in.
 *
 * Every approval is one change in History, so the banner it leaves offers
 * Undo: the change reverted and the request back in the queue.
 */
export function FoodRequestsScreen({ search, onSearchChange, onOpenRequest }: FoodRequestsScreenProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const status = search.status ?? 'open';
  const [draft, setDraft] = useState(search.q ?? '');
  const q = useDebounced(draft.trim(), 300);
  const [notice, setNotice] = useState<QueueNotice | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const hintId = useId();
  const selectHelpId = useId();
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if ((search.q ?? '') !== q) onSearchChange({ ...search, q: q === '' ? undefined : q });
    // Only the settled search drives the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const summary = useQuery(summaryQueryOptions(api));
  const list = useQuery(
    queueQueryOptions(api, { status, kind: search.kind, confidence: search.confidence, q: search.q }),
  );
  // The research footer and the empty queue read every open request, whatever the filters.
  const everyOpen = useQuery(queueQueryOptions(api, { status: 'open' }));

  const rows = useMemo(() => list.data ?? [], [list.data]);
  const researching = useMemo(
    () => (everyOpen.data ?? []).filter((request) => request.draftStatus === 'queued'),
    [everyOpen.data],
  );
  const shown = status === 'open' ? rows.filter((request) => request.draftStatus !== 'queued') : rows;
  const ready = status === 'open' ? shown.filter((request) => request.readyToApprove) : [];
  const look = status === 'open' ? shown.filter((request) => !request.readyToApprove) : shown;
  const selectable = new Set(ready.filter((request) => request.kind === 'name').map((request) => request.id));
  const chosen = [...selected].filter((id) => selectable.has(id));
  const filtered = search.kind !== undefined || search.confidence !== undefined || search.q !== undefined;

  const settle = (next: QueueNotice) => {
    setNotice(next);
    setSelected(new Set());
    invalidateRequests(queryClient);
    invalidateCatalog(queryClient);
  };

  const addName = useMutation({
    mutationFn: (request: AdminFoodRequest) => approveName(api, request.id, { as: 'alias' }),
    onSuccess: (approval, request) => {
      settle({
        text: `Added ${capitalise(request.name)} as a name for ${request.foodName ?? 'its food'}.`,
        undo: [{ batchId: approval.batchId, requestId: request.id }],
      });
    },
    retry: false,
  });

  const addNames = useMutation({
    mutationFn: (ids: readonly string[]) => approveNames(api, ids),
    onSuccess: (result) => {
      const refused =
        result.refused.length === 0
          ? ''
          : ` ${plural(result.refused.length, 'was', 'were')} not added: ${result.refused.map((entry) => entry.reason).join(' ')}`;
      settle({
        text: `Added ${plural(result.added.length, 'name')}.${refused}`,
        undo: result.added.map((added) => ({ batchId: added.batchId, requestId: added.id })),
      });
    },
    retry: false,
  });

  const retry = useMutation({
    mutationFn: (request: AdminFoodRequest) => researchAgain(api, request.id, ''),
    onSuccess: (_result, request) => {
      settle({ text: `Researching ${request.name} again. It comes back here when its sources are in.` });
    },
    retry: false,
  });

  const undo = useMutation({
    mutationFn: (approvals: NonNullable<QueueNotice['undo']>) => undoApprovals(api, approvals),
    onSuccess: () => {
      setNotice(null);
      invalidateRequests(queryClient);
      invalidateCatalog(queryClient);
    },
    retry: false,
  });

  const failure = addName.error ?? addNames.error ?? retry.error ?? undo.error;

  const open = (request: AdminFoodRequest) => {
    if (request.kind === 'name') onSearchChange({ ...search, open: request.id });
    else onOpenRequest(request.id);
  };

  /** J and K move between rows, A adds the focused row's name, Enter opens it (the link's own). */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement;
    if (target.closest('input, textarea, select') !== null) return;
    const links = [...(tableRef.current?.querySelectorAll<HTMLAnchorElement>('[data-row-link]') ?? [])];
    const at = links.findIndex((link) => link.contains(target) || target.contains(link) || link === target);
    const key = event.key.toLowerCase();
    if (key === 'j' || key === 'k') {
      event.preventDefault();
      const next = key === 'j' ? Math.min(links.length - 1, at + 1) : Math.max(0, at === -1 ? 0 : at - 1);
      links[next]?.focus();
    } else if (key === 'a' && at !== -1) {
      const request = shown.find((row) => row.id === links[at]?.dataset.rowLink);
      if (request !== undefined && request.readyToApprove && request.kind === 'name' && can.mutate) {
        event.preventDefault();
        addName.mutate(request);
      }
    }
  };

  const pills = (
    [
      ['all', 'All', summary.data?.open],
      ['new_food', 'New food', summary.data?.openByKind.new_food],
      ['name', 'Names', summary.data?.openByKind.name],
      ['fix', 'Fixes', summary.data?.openByKind.fix],
    ] as const
  ).map(([id, label, count]) =>
    // Open counts only: beside "Approved" they would count something else than the list shows.
    count === undefined || status !== 'open' ? { id, label } : { id, label, count },
  );

  const showEmpty =
    status === 'open' && !filtered && list.isSuccess && shown.length === 0 && search.view === undefined;

  return (
    <div className="mx-auto flex w-full max-w-[1300px] flex-col gap-3.5">
      {summary.data === undefined || search.view !== undefined ? null : (
        <p className="text-[13px] text-muted-foreground tabular-nums">
          <span className="font-medium text-foreground">{summary.data.open} open</span> ·{' '}
          {summary.data.readyToApprove} ready to approve
          {summary.data.oldestOpenAt === null
            ? ''
            : ` · oldest waiting ${daysWaiting(summary.data.oldestOpenAt)}`}{' '}
          · {plural(summary.data.logsOnEstimates, 'meal')} on AI estimates
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <ScopePills<KindScope>
          label="Request kind"
          scopes={pills}
          value={search.view === 'names' ? 'name' : (search.kind ?? 'all')}
          onChange={(next) => {
            onSearchChange({ ...search, kind: next === 'all' ? undefined : next, view: undefined });
          }}
        />
        {search.view === 'names' ? null : (
          <>
            <FilterSelect
              label="Status"
              value={status}
              options={STATUS_OPTIONS}
              onChange={(next) => {
                onSearchChange({ ...search, status: next === 'open' ? undefined : (next as QueueStatus) });
              }}
            />
            <label className="relative flex max-w-[340px] flex-[1_1_220px] items-center">
              <span className="sr-only">Search what people typed</span>
              <SearchIcon
                aria-hidden="true"
                className="absolute left-2.5 size-[15px] text-muted-foreground"
              />
              <Input
                type="search"
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                }}
                placeholder="Search what people typed or the food"
                className="h-8 rounded-lg pl-8 text-[13px]"
              />
            </label>
          </>
        )}
        <FilterSelect
          label="Confidence"
          value={search.view === 'names' ? 'high' : (search.confidence ?? ANY)}
          options={CONFIDENCE_OPTIONS}
          onChange={(next) => {
            onSearchChange({
              ...search,
              view: undefined,
              confidence: next === ANY ? undefined : (next as RecipeConfidenceName),
            });
          }}
        />
      </div>

      {notice === null ? null : (
        <StatusBanner
          action={
            notice.undo === undefined || notice.undo.length === 0 ? undefined : (
              <Button
                variant="outline"
                size="sm"
                disabled={undo.isPending || !can.mutate}
                className="border-ok-fg/25"
                onClick={() => {
                  if (notice.undo !== undefined) undo.mutate(notice.undo);
                }}
              >
                {undo.isPending ? 'Undoing…' : 'Undo'}
              </Button>
            )
          }
        >
          {notice.text}
        </StatusBanner>
      )}

      {failure === null ? null : (
        <Alert role="alert" tone="err">
          <AlertDescription>{failure.message}</AlertDescription>
        </Alert>
      )}
      {list.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{list.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {search.view === 'names' ? (
        <BulkNames
          onBack={() => {
            onSearchChange({ ...search, view: undefined });
          }}
          onOpen={(id) => {
            onSearchChange({ ...search, open: id });
          }}
          onAdded={settle}
          notice={notice}
        />
      ) : showEmpty ? (
        <EmptyQueue
          researching={researching}
          onShowAnswered={() => {
            onSearchChange({ ...search, status: 'resolved' });
          }}
        />
      ) : (
        <section
          aria-labelledby="queue-title"
          className="overflow-hidden rounded-xl border border-border bg-card"
        >
          <header className="flex flex-wrap items-center gap-3 px-[18px] pt-3.5 pb-3">
            <h2 id="queue-title" className="text-[15px] font-semibold text-foreground">
              {STATUS_TITLES[status]}
            </h2>
            <span
              aria-label="Keyboard: J and K move between rows, A adds the name, Enter opens the request"
              className="ml-auto flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
            >
              <span aria-hidden="true" className="contents">
                <Kbd>J</Kbd>
                <Kbd>K</Kbd>
                <span>move ·</span>
                <Kbd>A</Kbd>
                <span>add name ·</span>
                <Kbd>Enter</Kbd>
                <span>open</span>
              </span>
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">showing {shown.length}</span>
          </header>

          {chosen.length === 0 ? null : (
            <SelectionBar
              requests={ready.filter((request) => chosen.includes(request.id))}
              isPending={addNames.isPending}
              canMutate={can.mutate}
              onClear={() => {
                setSelected(new Set());
              }}
              onAdd={() => {
                addNames.mutate(chosen);
              }}
            />
          )}

          <div className="overflow-x-auto border-t border-border">
            <div
              ref={tableRef}
              role="table"
              aria-label={
                status === 'open' ? 'Open food requests, ready to approve first' : STATUS_TITLES[status]
              }
              aria-describedby={hintId}
              className="min-w-[900px]"
              onKeyDown={onKeyDown}
            >
              <div role="rowgroup">
                <div role="row" className={cn(GRID, 'min-h-9 bg-secondary py-1')}>
                  <span role="columnheader" className="pl-2.5">
                    <label className="flex size-8 cursor-pointer items-center justify-center">
                      <input
                        type="checkbox"
                        aria-label="Select every name ready to approve"
                        disabled={selectable.size === 0}
                        checked={selectable.size > 0 && chosen.length === selectable.size}
                        onChange={(event) => {
                          setSelected(event.target.checked ? new Set(selectable) : new Set());
                        }}
                        className="size-4 cursor-pointer accent-primary disabled:cursor-not-allowed"
                      />
                    </label>
                  </span>
                  <span role="columnheader" className={HEADER_CELL}>
                    What people typed
                  </span>
                  <span role="columnheader" className={HEADER_CELL}>
                    Kind
                  </span>
                  <span role="columnheader" className={HEADER_CELL}>
                    {status === 'resolved' || status === 'rejected'
                      ? 'What happened'
                      : 'What the AI proposes'}
                  </span>
                  <span role="columnheader" className={HEADER_CELL}>
                    Confidence
                  </span>
                  <span role="columnheader" className={HEADER_CELL}>
                    People
                  </span>
                  <span role="columnheader" className={HEADER_CELL}>
                    Waiting
                  </span>
                  <span role="columnheader" className={cn(HEADER_CELL, 'pr-[18px] text-right')}>
                    Action
                  </span>
                </div>
              </div>

              {list.isPending ? (
                <p className="border-t border-border px-[18px] py-6 text-[13px] text-muted-foreground">
                  Loading…
                </p>
              ) : shown.length === 0 ? (
                <p className="border-t border-border px-[18px] py-6 text-[13px] text-muted-foreground">
                  No requests match these filters.
                </p>
              ) : null}

              {ready.length === 0 ? null : (
                <div role="rowgroup">
                  <GroupRow
                    title="Ready to approve"
                    count={ready.length}
                    note="High confidence, nothing to check. One click each."
                    action={
                      ready.some((request) => request.kind === 'name') ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            onSearchChange({ ...search, view: 'names', open: undefined });
                          }}
                        >
                          Select all {selectable.size}
                        </Button>
                      ) : undefined
                    }
                  />
                  {ready.map((request) => (
                    <QueueRow
                      key={request.id}
                      request={request}
                      selectable={selectable.has(request.id)}
                      selected={selected.has(request.id)}
                      selectHelpId={selectHelpId}
                      canMutate={can.mutate}
                      isAdding={addName.isPending && addName.variables.id === request.id}
                      onSelect={(on) => {
                        setSelected((current) => {
                          const next = new Set(current);
                          if (on) next.add(request.id);
                          else next.delete(request.id);
                          return next;
                        });
                      }}
                      onOpen={() => {
                        open(request);
                      }}
                      onAddName={() => {
                        addName.mutate(request);
                      }}
                      onRetry={() => {
                        retry.mutate(request);
                      }}
                    />
                  ))}
                </div>
              )}

              {look.length === 0 ? null : (
                <div role="rowgroup">
                  {status === 'open' ? (
                    <GroupRow
                      title="Needs a look"
                      count={look.length}
                      note="Most people affected first. Each review opens on the fields to check."
                    />
                  ) : null}
                  {look.map((request) => (
                    <QueueRow
                      key={request.id}
                      request={request}
                      selectable={false}
                      selected={false}
                      selectHelpId={selectHelpId}
                      canMutate={can.mutate}
                      isAdding={false}
                      isRetrying={retry.isPending && retry.variables.id === request.id}
                      onSelect={() => undefined}
                      onOpen={() => {
                        open(request);
                      }}
                      onAddName={() => undefined}
                      onRetry={() => {
                        retry.mutate(request);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border bg-secondary/40 px-[18px] py-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-info-fg" />
              {researchingText(researching)}
            </span>
            <span id={selectHelpId} className="sr-only">
              Only name requests with high confidence and no clash can be selected together. New foods and
              fixes always open one at a time.
            </span>
            <span id={hintId} className="ml-auto">
              A request opens itself when a meal is logged as an AI estimate or matched by another name.
            </span>
          </div>
        </section>
      )}

      {can.mutate ? null : <p className="text-xs text-muted-foreground">{ADMIN_ONLY}</p>}

      {search.open === undefined ? null : (
        <NameRequestDrawer
          requestId={search.open}
          next={nextName(rows, search.open)}
          onClose={() => {
            onSearchChange({ ...search, open: undefined });
          }}
          onOpenRequest={(id) => {
            onSearchChange({ ...search, open: id });
          }}
          onAnswered={(next) => {
            settle(next);
          }}
        />
      )}
    </div>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The next name request in the queue after this one, for "Then opens the next one". */
function nextName(rows: readonly AdminFoodRequest[], id: string): AdminFoodRequest | null {
  const names = rows.filter((request) => request.kind === 'name' && request.status === 'open');
  const at = names.findIndex((request) => request.id === id);
  return names.find((request, index) => index > at && request.id !== id) ?? null;
}

function researchingText(researching: readonly AdminFoodRequest[]): string {
  if (researching.length === 0) return 'Nothing is being researched right now.';
  const names = researching.map((request) => request.name).join(', ');
  return researching.length === 1
    ? `1 more is being researched: ${names}. It joins the queue when its sources are in.`
    : `${String(researching.length)} more are being researched: ${names}. They join the queue when their sources are in.`;
}

function GroupRow({
  title,
  count,
  note,
  action,
}: {
  readonly title: string;
  readonly count: number;
  readonly note: string;
  readonly action?: ReactNode;
}) {
  return (
    <div
      role="row"
      className="flex min-h-11 flex-wrap items-center gap-x-2.5 gap-y-1.5 border-t border-border bg-secondary/50 px-[18px] py-2"
    >
      <span role="cell" className="flex flex-auto flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <span className="text-[13px] font-semibold text-foreground">{title}</span>
        <span className="font-mono text-xs text-muted-foreground tabular-nums">{count}</span>
        <span className="text-xs text-muted-foreground">{note}</span>
        {action === undefined ? null : <span className="ml-auto">{action}</span>}
      </span>
    </div>
  );
}

interface QueueRowProps {
  readonly request: AdminFoodRequest;
  readonly selectable: boolean;
  readonly selected: boolean;
  readonly selectHelpId: string;
  readonly canMutate: boolean;
  readonly isAdding: boolean;
  readonly isRetrying?: boolean;
  readonly onSelect: (on: boolean) => void;
  readonly onOpen: () => void;
  readonly onAddName: () => void;
  readonly onRetry: () => void;
}

function QueueRow({
  request,
  selectable,
  selected,
  selectHelpId,
  canMutate,
  isAdding,
  isRetrying = false,
  onSelect,
  onOpen,
  onAddName,
  onRetry,
}: QueueRowProps) {
  const failed = request.draftStatus === 'failed';
  const answered = request.status !== 'open';
  const age = waited(request.createdAt);
  const level = confidenceOf(request);
  const href = request.kind === 'name' ? `?open=${request.id}` : `/food-requests/${request.id}`;

  return (
    <div
      role="row"
      className={cn(GRID, 'min-h-14 border-t border-border', failed ? 'bg-err-bg/40' : 'bg-card')}
    >
      <span role="cell" className="pl-2.5">
        <label className={cn('flex size-8 items-center justify-center', selectable && 'cursor-pointer')}>
          <input
            type="checkbox"
            aria-label={`Select ${request.name}`}
            aria-describedby={selectable ? undefined : selectHelpId}
            disabled={!selectable}
            checked={selected}
            onChange={(event) => {
              onSelect(event.target.checked);
            }}
            className="size-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
          />
        </label>
      </span>
      <span role="cell" className="flex min-w-0 flex-col gap-0.5 px-3 py-2">
        <a
          href={href}
          data-row-link={request.id}
          onClick={(event) => {
            event.preventDefault();
            onOpen();
          }}
          className="inline-flex min-h-6 items-center self-start font-medium text-foreground no-underline hover:text-destructive"
        >
          {request.name}
        </a>
        <span className="text-xs text-muted-foreground">{typedSubOf(request)}</span>
      </span>
      <span role="cell" className="px-3 py-2">
        <KindBadge kind={request.kind} />
      </span>
      <span role="cell" className="flex min-w-0 flex-col gap-0.5 px-3 py-2">
        <span
          className={cn(
            'inline-flex items-start gap-1.5 text-[13px] leading-snug',
            failed ? 'font-medium text-err-fg' : 'text-foreground',
          )}
        >
          {failed ? <AlertCircleIcon aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" /> : null}
          {failed ? 'Research found no sources' : answered ? answerLine(request) : proposalLine(request)}
        </span>
        <span className="text-xs text-muted-foreground">
          {answered
            ? request.answeredAt === null
              ? ''
              : `Answered ${waitedText(request.answeredAt)} ago`
            : proposalSubOf(request)}
        </span>
      </span>
      <span role="cell" className="flex flex-col gap-px px-3 py-2 text-[13px]">
        <ConfidenceDot level={level} />
        <span
          className={cn(
            'text-xs',
            failed
              ? 'text-err-fg'
              : request.readyToApprove || answered
                ? 'text-muted-foreground'
                : 'text-warn-fg',
          )}
        >
          {answered ? '' : checksOf(request)}
        </span>
      </span>
      <span role="cell" className="flex flex-col gap-px px-3 py-2">
        <span className="font-mono text-[13px] tabular-nums">{request.userCount}</span>
        <span className="text-xs text-muted-foreground">{request.userCount === 1 ? 'person' : 'people'}</span>
      </span>
      <span role="cell" className="px-3 py-2 text-[13px] text-muted-foreground">
        <span className="font-mono tabular-nums">{age.num}</span> {age.unit}
      </span>
      <span role="cell" className="flex justify-end py-2 pr-[18px] pl-2">
        {failed && !answered ? (
          <Button
            variant="outline"
            size="sm"
            aria-label={`Research ${request.name} again`}
            disabled={!canMutate || isRetrying}
            onClick={onRetry}
          >
            <RotateCcwIcon aria-hidden="true" className="size-3.5" />
            {isRetrying ? 'Queuing…' : 'Retry'}
          </Button>
        ) : request.readyToApprove && request.kind === 'name' && !answered ? (
          <Button
            variant="outline"
            size="sm"
            aria-label={`Add ${request.name} as a name for ${request.foodName ?? 'its food'}`}
            disabled={!canMutate || isAdding}
            onClick={onAddName}
          >
            <CheckIcon aria-hidden="true" className="size-3.5 text-ok-fg" />
            {isAdding ? 'Adding…' : 'Add name'}
          </Button>
        ) : (
          <Button variant="outline" size="sm" asChild>
            <a
              href={href}
              aria-label={`${answered ? 'Open' : 'Review'} ${request.name}`}
              onClick={(event) => {
                event.preventDefault();
                onOpen();
              }}
            >
              {answered ? 'Open' : 'Review'}
              <ArrowRightIcon aria-hidden="true" className="size-3.5" />
            </a>
          </Button>
        )}
      </span>
    </div>
  );
}

function waitedText(iso: string): string {
  const age = waited(iso);
  return `${age.num} ${age.unit}`;
}

function proposalLine(request: AdminFoodRequest) {
  return <Figures text={request.proposal.replace(/"([^"]+)"/g, '“$1”')} />;
}

function answerLine(request: AdminFoodRequest): string {
  if (request.status === 'rejected') return `Rejected: ${request.rejectionReason ?? 'no reason given'}`;
  return `${KIND_LABELS[request.kind]} approved: ${request.resolvedFoodName ?? 'a food since removed'}`;
}

function SelectionBar({
  requests,
  isPending,
  canMutate,
  onClear,
  onAdd,
}: {
  readonly requests: readonly AdminFoodRequest[];
  readonly isPending: boolean;
  readonly canMutate: boolean;
  readonly onClear: () => void;
  readonly onAdd: () => void;
}) {
  const foods = new Set(requests.map((request) => request.foodId)).size;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-2.5 gap-y-2 border-t border-border bg-secondary py-2 pr-3 pl-[18px]"
    >
      <span className="font-medium text-foreground">{requests.length} selected</span>
      <span className="text-xs text-muted-foreground">
        Adds {plural(requests.length, 'name')} to {plural(foods, 'food')}. No numbers change and no logs move.
      </span>
      <span className="ml-auto flex items-center gap-1.5">
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear
        </Button>
        <Button size="sm" disabled={!canMutate || isPending} onClick={onAdd}>
          <CheckIcon aria-hidden="true" className="size-3.5" />
          {isPending ? 'Adding…' : `Add ${plural(requests.length, 'name')}`}
        </Button>
      </span>
    </div>
  );
}
